<template>
  <div class="gap-2 items-center hidden sm:flex">
    <div class="hidden sm:flex items-center gap-2 min-w-0 flex-1">
      <VaIconMenuCollapsed
        class="cursor-pointer flex-shrink-0"
        :class="{ 'x-flip': !settings.isSidebarMinimized }"
        :color="collapseIconColor"
        @click="store.toggleSidebar"
      />

      <nav class="flex items-center min-w-0 overflow-hidden">
        <VaBreadcrumbs>
          <VaBreadcrumbsItem :label="t('general.home')" :to="{ name: 'home' }" />
          <VaBreadcrumbsItem
            v-for="item in items"
            :key="item.label"
            :label="item.label"
            :to="item.to"
            @click="handleBreadcrumbClick(item)"
          />
        </VaBreadcrumbs>
      </nav>
    </div>

    <HelpButton v-if="!pageTitlePresent" class="ml-auto mr-4" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useI18n } from "vue-i18n"
import { useColors } from "vuestic-ui"
import VaIconMenuCollapsed from "@shared/components/icons/VaIconMenuCollapsed.vue"
import { storeToRefs } from "pinia"
import { useGlobalStore } from "../../stores/global-store"
import { usePageTitlePresence } from "@shared/composables/usePageTitlePresence"

const { isPresent: pageTitlePresent } = usePageTitlePresence()

const { settings } = storeToRefs(useGlobalStore())
const store = useGlobalStore()

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

type BreadcrumbNavigationItem = {
  label: string
  to: string
  hasChildren: boolean
}

const items = computed(() => {
  const result: { label: string; to: string; hasChildren: boolean }[] = []
  const currentRouteName = route.name as string
  const menuHierarchy = store.menuHierarchy
  route.matched.forEach((route) => {
    let menuItem = menuHierarchy.find((item) => item.route === route.name)
    if (!menuItem) {
      const parentRouteName = route.meta.parentRoute as string | undefined
      if (parentRouteName) {
        menuItem = menuHierarchy.find((item) => item.route === parentRouteName)
      }
    }
    if (!menuItem || !menuItem.displayNameKey) {
      return
    }
    result.push({
      label: t(menuItem.displayNameKey as string),
      to: route.name && currentRouteName !== route.name ? route.path : "",
      hasChildren: route.children && route.children.length > 0,
    })
  })

  // Append a dynamic leaf crumb (e.g. the current Help page title) when set by the page.
  if (store.breadcrumbLeaf) {
    result.push({ label: store.breadcrumbLeaf, to: "", hasChildren: false })
  }

  return result
})

const { getColor } = useColors()

const collapseIconColor = computed(() => getColor("secondary"))

const handleBreadcrumbClick = (item: BreadcrumbNavigationItem) => {
  if (!item.hasChildren) {
    router.push(item.to)
  }
}
</script>

<style lang="scss" scoped>
.x-flip {
  transform: scaleX(-100%);
}
</style>
